import { ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";
import { clsx } from "clsx";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Case } from "@/api/rest/models/warranty";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/common/Accordeon";
import { InspectionGroup } from "@/utils/inspectionGroups";

/**
 * One end-of-warranty inspection as a collapsible row. The header summarises progress so a
 * collapsed group still answers "what is left?"; the defects render inside in act order.
 */
export default function InspectionCaseGroup({
  group,
  householdTitle,
  attentionCount,
  defaultOpen,
  showExternalNumber,
  renderCase,
}: {
  group: InspectionGroup;
  householdTitle?: string;
  attentionCount: number;
  defaultOpen: boolean;
  showExternalNumber: boolean;
  renderCase: (c: Case) => ReactNode;
}) {
  const { t } = useTranslation();
  const value = `inspection-${group.inspection.id}`;
  const total = group.cases.length;
  const progress = Math.round((group.completedCount / total) * 100);
  const progressLabel = t("warrantyInspection.progress", { done: group.completedCount, total });

  return (
    <Accordion type="single" collapsible defaultValue={defaultOpen ? value : undefined} className="max-w-[800px]">
      <AccordionItem value={value} className={clsx("rounded-md !border-none", attentionCount > 0 && "!bg-orange-200")}>
        <AccordionTrigger
          className={clsx(
            "gap-2 rounded-md !py-2 !pl-4 !pr-2 text-left !leading-normal !cursor-pointer",
            attentionCount > 0 && "hover:!bg-orange-300",
          )}
        >
          <ClipboardDocumentCheckIcon className="h-5 w-5 shrink-0 text-dobu-orange" aria-hidden />
          <div className="flex flex-col flex-1 gap-1 min-w-0">
            <span className="font-bold">
              {householdTitle && `${householdTitle} `}
              {t("warrantyInspection.title")}
            </span>
            <div className="flex flex-row flex-wrap items-center gap-x-2 text-sm text-gray-500">
              <span>{progressLabel}</span>
              {attentionCount > 0 && (
                <span className="font-medium text-dobu-orange">
                  · {t("warrantyInspection.needsAttention", { count: attentionCount })}
                </span>
              )}
              {showExternalNumber && group.inspection.externalNumber && (
                <span>· Hausing #{group.inspection.externalNumber}</span>
              )}
              <span className="flex-grow" />
              <span>{new Date(group.inspection.inspectedAt).toLocaleDateString()}</span>
            </div>
            <div
              role="progressbar"
              aria-label={progressLabel}
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              className="h-1 w-full rounded bg-gray-200"
            >
              <div className="h-1 rounded bg-green-500" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent>
          <div className="flex flex-col gap-2 border-l-2 border-orange-200 pl-3">{group.cases.map(renderCase)}</div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
